import {
    type CSSProperties,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Collapsible,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Collapsible";

const USAGE_CODE = `<Collapsible>
    <Collapsible.Trigger
        openTitle="Hide advanced settings"
        title="Show advanced settings"
    />
    <Collapsible.Content>
        <TextInput
            title="Project code"
        />
    </Collapsible.Content>
</Collapsible>`;

const CONTROLLED_CODE = `const [isOpen, setIsOpen] = useState(false);

<Collapsible
    onOpenChange={setIsOpen}
    isOpen={isOpen}
>
    <Collapsible.Trigger/>
    <Collapsible.Content>
        <Text>Controlled content</Text>
    </Collapsible.Content>
</Collapsible>`;

const PLACEMENT_CODE = `<Collapsible>
    {visibleRows}
    <Collapsible.Content>
        {hiddenRows}
    </Collapsible.Content>
    <Collapsible.Trigger
        openTitle="Show less"
        title="Show 3 more"
    />
</Collapsible>`;

const KEEP_MOUNTED_CODE = `<Collapsible.Content
    isKeepMounted={true}
>
    <TextInput
        title="Project code"
    />
</Collapsible.Content>`;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("collapsiblePage.labelsTitle"),
        id: "labels"
    },
    {
        title: t("collapsiblePage.controlledTitle"),
        id: "controlled"
    },
    {
        title: t("collapsiblePage.placementTitle"),
        id: "placement"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("collapsiblePage.keepMountedTitle"),
        id: "keep-mounted"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CollapsiblePage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isControlledOpen,
        setIsControlledOpen
    ] = useState(false);

    const visibleMembers = [
        t("collapsiblePage.member1"),
        t("collapsiblePage.member2")
    ];

    const hiddenMembers = [
        t("collapsiblePage.member3"),
        t("collapsiblePage.member4"),
        t("collapsiblePage.member5")
    ];

    const listStyle: CSSProperties = {
        paddingBottom: spaces.spacingMd,
        flexDirection: "column",
        gap: spaces.spacingMd,
        display: "flex"
    };

    const handleToggleControlled = () => setIsControlledOpen(!isControlledOpen);

    const renderMember = (member: string) => <Text
        variant="bodyMediumSize"
        key={member}
        color="high"
    >
        {member}
    </Text>;

    const renderNote = (text: string) => <Text
        variant="bodyMediumSize"
        color="mid"
    >
        {text}
    </Text>;

    return <Article>
        <ComponentHeader
            lead={t("collapsiblePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("collapsiblePage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("collapsiblePage.overviewCaption")}
                isStacked={true}
            >
                <Collapsible>
                    <Collapsible.Trigger
                        openTitle={t("collapsiblePage.hideAdvanced")}
                        title={t("collapsiblePage.showAdvanced")}
                    />
                    <Collapsible.Content>
                        <TextInput
                            title={t("collapsiblePage.projectCode")}
                        />
                    </Collapsible.Content>
                </Collapsible>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("collapsiblePage.labelsTitle")}
            id="labels"
        >
            <Paragraph>
                {t("collapsiblePage.labelsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Collapsible
                    isInitiallyOpen={true}
                >
                    <Collapsible.Trigger/>
                    <Collapsible.Content>
                        {renderNote(t("collapsiblePage.defaultLabelNote"))}
                    </Collapsible.Content>
                </Collapsible>
                <Collapsible>
                    <Collapsible.Trigger
                        title={t("collapsiblePage.details")}
                    />
                    <Collapsible.Content>
                        {renderNote(t("sample.body"))}
                    </Collapsible.Content>
                </Collapsible>
                <Collapsible>
                    <Collapsible.Trigger
                        openTitle={t("collapsiblePage.less")}
                        title={t("collapsiblePage.more")}
                    />
                    <Collapsible.Content>
                        {renderNote(t("sample.body"))}
                    </Collapsible.Content>
                </Collapsible>
            </Preview>
        </Section>
        <Section
            title={t("collapsiblePage.controlledTitle")}
            id="controlled"
        >
            <Paragraph>
                {t("collapsiblePage.controlledText")}
            </Paragraph>
            <Preview
                caption={t("collapsiblePage.controlledCaption", [
                    isControlledOpen ? t("sample.stateOpen") : t("sample.stateClosed")
                ])}
                isStacked={true}
            >
                <Collapsible
                    onOpenChange={setIsControlledOpen}
                    isOpen={isControlledOpen}
                >
                    <Collapsible.Trigger/>
                    <Collapsible.Content>
                        {renderNote(t("collapsiblePage.controlledNote"))}
                    </Collapsible.Content>
                </Collapsible>
                <Button
                    title={isControlledOpen ? t("collapsiblePage.closeFromOutside") : t("collapsiblePage.openFromOutside")}
                    onPress={handleToggleControlled}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("collapsiblePage.placementTitle")}
            id="placement"
        >
            <Paragraph>
                {t("collapsiblePage.placementText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Collapsible>
                    <div
                        style={listStyle}
                    >
                        {visibleMembers.map(renderMember)}
                    </div>
                    <Collapsible.Content>
                        {hiddenMembers.map(renderMember)}
                    </Collapsible.Content>
                    <Collapsible.Trigger
                        title={t("collapsiblePage.showMore", [
                            hiddenMembers.length
                        ])}
                        openTitle={t("collapsiblePage.less")}
                    />
                </Collapsible>
            </Preview>
            <CodeBlock
                code={PLACEMENT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("collapsiblePage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Collapsible
                    isDisabled={true}
                >
                    <Collapsible.Trigger
                        title={t("collapsiblePage.lockedClosed")}
                    />
                    <Collapsible.Content>
                        {renderNote(t("sample.body"))}
                    </Collapsible.Content>
                </Collapsible>
                <Collapsible
                    isInitiallyOpen={true}
                    isDisabled={true}
                >
                    <Collapsible.Trigger
                        title={t("collapsiblePage.lockedOpen")}
                    />
                    <Collapsible.Content>
                        {renderNote(t("sample.body"))}
                    </Collapsible.Content>
                </Collapsible>
            </Preview>
        </Section>
        <Section
            title={t("collapsiblePage.keepMountedTitle")}
            id="keep-mounted"
        >
            <Paragraph>
                {t("collapsiblePage.keepMountedText")}
            </Paragraph>
            <Preview
                caption={t("collapsiblePage.keepMountedCaption")}
                isStacked={true}
            >
                <Collapsible
                    isInitiallyOpen={true}
                >
                    <Collapsible.Trigger
                        title={t("collapsiblePage.keepMountedOff")}
                    />
                    <Collapsible.Content>
                        <TextInput
                            title={t("collapsiblePage.projectCode")}
                        />
                    </Collapsible.Content>
                </Collapsible>
                <Collapsible
                    isInitiallyOpen={true}
                >
                    <Collapsible.Trigger
                        title={t("collapsiblePage.keepMountedOn")}
                    />
                    <Collapsible.Content
                        isKeepMounted={true}
                    >
                        <TextInput
                            title={t("collapsiblePage.projectCode")}
                        />
                    </Collapsible.Content>
                </Collapsible>
            </Preview>
            <CodeBlock
                code={KEEP_MOUNTED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("collapsiblePage.a11yTrigger"),
                    t("collapsiblePage.a11yKeyboard"),
                    t("collapsiblePage.a11yLabel"),
                    t("collapsiblePage.a11yFocus"),
                    t("collapsiblePage.a11yDisabled"),
                    t("collapsiblePage.a11yHidden"),
                    t("collapsiblePage.a11yMotion"),
                    t("collapsiblePage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("collapsiblePage.propIsOpen"),
                        type: "boolean",
                        name: "isOpen"
                    },
                    {
                        description: t("collapsiblePage.propIsInitiallyOpen"),
                        name: "isInitiallyOpen",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("collapsiblePage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("collapsiblePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("collapsiblePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("collapsiblePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("collapsiblePage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Collapsible.Trigger
                </InlineCode>
                {" "}
                {t("collapsiblePage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("collapsiblePage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("collapsiblePage.propOpenTitle"),
                        name: "openTitle",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("collapsiblePage.propTriggerCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("collapsiblePage.propTriggerCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("collapsiblePage.triggerNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Collapsible.Content
                </InlineCode>
                {" "}
                {t("collapsiblePage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("collapsiblePage.propIsKeepMounted"),
                        defaultValue: "false",
                        name: "isKeepMounted",
                        type: "boolean"
                    },
                    {
                        description: t("collapsiblePage.propContentContainerStyle"),
                        name: "contentContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("collapsiblePage.propContentCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("collapsiblePage.contentNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CollapsiblePage;
